'use client';
import React, { useState, useCallback } from 'react';
import { Search, Play, Video, Eye, Heart, ArrowRight, ChevronRight, Clock, Upload } from 'lucide-react';
import Link from 'next/link';
import AppLayout from '@/components/AppLayout';

interface VideoItem {
  id: string;
  title: string;
  creator: string;
  category: string;
  views: number;
  duration: string;
  posted: string;
  thumbnail: string;
  is_featured: boolean;
  tags: string[];
}

const VIDEOS: VideoItem[] = [
{ id: '1', title: 'How to Start a Business in Kenya — Complete Guide 2026', creator: 'BizKenya', category: 'Business', views: 145000, duration: '18:32', posted: '2 days ago', thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_17ca6ec5e-1768307092281.png", is_featured: true, tags: ['Business', 'Entrepreneurship', 'Kenya'] },
{ id: '2', title: 'Nairobi Street Food Tour — 10 Must-Try Dishes', creator: 'FoodieKenya', category: 'Food', views: 89000, duration: '12:15', posted: '3 days ago', thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_119e77b82-1784558009167.png", is_featured: true, tags: ['Food', 'Nairobi', 'Street Food'] },
{ id: '3', title: 'Maasai Mara Safari Highlights — July 2026', creator: 'WildKenya', category: 'Travel', views: 234000, duration: '22:45', posted: '1 week ago', thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1f82de34b-1784558010007.png", is_featured: false, tags: ['Safari', 'Wildlife', 'Travel'] },
{ id: '4', title: 'Python Programming Tutorial for Beginners (Swahili)', creator: 'TechNairobi', category: 'Education', views: 67000, duration: '45:00', posted: '5 days ago', thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1d5cdeb9e-1773227311717.png", is_featured: false, tags: ['Python', 'Programming', 'Tutorial'] },
{ id: '5', title: 'Gor Mahia vs AFC Leopards — Full Match Highlights', creator: 'KPLOfficial', category: 'Sports', views: 312000, duration: '8:20', posted: '1 day ago', thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_11fdf3594-1777018762449.png", is_featured: false, tags: ['Football', 'KPL', 'Highlights'] },
{ id: '6', title: 'Mombasa Beach Vlog — Hidden Gems You Must Visit', creator: 'CoastVibes', category: 'Travel', views: 45000, duration: '15:30', posted: '4 days ago', thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_17f76b966-1784558009127.png", is_featured: false, tags: ['Mombasa', 'Beach', 'Travel'] }];


const CATEGORIES = ['All', 'Business', 'Food', 'Travel', 'Education', 'Sports', 'Entertainment', 'Music'];
const SORT_OPTIONS = ['Most Viewed', 'Latest', 'Trending'];

export default function VideosPage() {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [sort, setSort] = useState('Most Viewed');
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());

  const filtered = VIDEOS.filter((v) => {
    const matchSearch = !search || v.title.toLowerCase().includes(search.toLowerCase()) || v.creator.toLowerCase().includes(search.toLowerCase()) || v.tags.some((t) => t.toLowerCase().includes(search.toLowerCase()));
    const matchCat = category === 'All' || v.category === category;
    return matchSearch && matchCat;
  }).sort((a, b) => {
    if (sort === 'Most Viewed') return b.views - a.views;
    return 0;
  });

  const toggleSave = useCallback((id: string) => {
    setSavedIds((prev) => {const n = new Set(prev);n.has(id) ? n.delete(id) : n.add(id);return n;});
  }, []);

  return (
    <AppLayout>
      <div className="min-h-screen" style={{ background: '#070C1A' }}>
        {/* Hero */}
        <div className="relative px-4 pt-8 pb-10 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #1a0a0a 0%, #0d1520 50%, #070C1A 100%)' }}>
          <div className="absolute inset-0 opacity-20"
          style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, #FF3B5C 0%, transparent 50%), radial-gradient(circle at 70% 30%, #FF6D00 0%, transparent 50%)' }} />
          <div className="relative max-w-2xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-600 mb-4"
            style={{ background: 'rgba(255,59,92,0.15)', color: '#FF3B5C', border: '1px solid rgba(255,59,92,0.3)' }}>
              <Video size={12} /> Kenya Video Platform
            </div>
            <h1 className="text-3xl font-800 text-white mb-3 leading-tight">
              Watch & Share <span style={{ color: '#FF3B5C' }}>Videos</span> from Kenya
            </h1>
            <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.55)' }}>
              Business, travel, sports, education and entertainment. Created by Kenyans, for Kenyans.
            </p>
            <div className="relative max-w-md mx-auto">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: 'rgba(255,255,255,0.4)' }} />
              <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search videos, creators, or topics…"
              className="w-full pl-10 pr-4 py-3.5 rounded-2xl text-sm outline-none"
              style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff' }} />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="px-4 py-3 sticky top-0 z-20"
        style={{ background: 'rgba(7,12,26,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="max-w-4xl mx-auto flex items-center gap-2 overflow-x-auto pb-1">
            {CATEGORIES.map((c) =>
            <button key={c} onClick={() => setCategory(c)}
            className="px-3 py-2 rounded-xl text-xs font-600 flex-shrink-0 transition-all"
            style={{ background: category === c ? 'rgba(255,59,92,0.15)' : 'rgba(255,255,255,0.04)', color: category === c ? '#FF3B5C' : 'rgba(255,255,255,0.55)', border: `1px solid ${category === c ? 'rgba(255,59,92,0.3)' : 'rgba(255,255,255,0.08)'}` }}>
                {c}
              </button>
            )}
            <div className="flex-shrink-0 w-px h-5 mx-1" style={{ background: 'rgba(255,255,255,0.1)' }} />
            {SORT_OPTIONS.map((s) =>
            <button key={s} onClick={() => setSort(s)}
            className="px-3 py-2 rounded-xl text-xs font-600 flex-shrink-0 transition-all"
            style={{ background: sort === s ? 'rgba(255,179,0,0.15)' : 'rgba(255,255,255,0.04)', color: sort === s ? '#FFB300' : 'rgba(255,255,255,0.55)', border: `1px solid ${sort === s ? 'rgba(255,179,0,0.3)' : 'rgba(255,255,255,0.08)'}` }}>
                {s}
              </button>
            )}
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 pt-4 pb-8">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
              <span style={{ color: '#FF3B5C', fontWeight: 700 }}>{filtered.length}</span> videos found
            </span>
            <Link href="/login"
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-700"
            style={{ background: '#FF3B5C', color: '#fff' }}>
              Upload Video <ArrowRight size={12} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {filtered.map((video) =>
            <div key={video.id} className="rounded-2xl overflow-hidden"
            style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${video.is_featured ? 'rgba(255,59,92,0.25)' : 'rgba(255,255,255,0.07)'}` }}>
                <div className="relative aspect-video">
                  <img src={video.thumbnail} alt={video.title}
                className="w-full h-full object-cover" loading="lazy" />
                  <div className="absolute inset-0 flex items-center justify-center"
                style={{ background: 'rgba(0,0,0,0.3)' }}>
                    <div className="w-12 h-12 rounded-full flex items-center justify-center"
                  style={{ background: 'rgba(255,59,92,0.85)' }}>
                      <Play size={18} fill="white" style={{ color: 'white', marginLeft: 2 }} />
                    </div>
                  </div>
                  <span className="absolute bottom-2 right-2 text-[10px] px-1.5 py-0.5 rounded-md font-700"
                style={{ background: 'rgba(0,0,0,0.8)', color: '#fff' }}>{video.duration}</span>
                  {video.is_featured &&
                <span className="absolute top-2 left-2 text-[9px] px-1.5 py-0.5 rounded-md font-700"
                style={{ background: '#FF3B5C', color: '#fff' }}>Featured</span>
                }
                  <button onClick={() => toggleSave(video.id)}
                className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center"
                style={{ background: savedIds.has(video.id) ? '#FF4081' : 'rgba(0,0,0,0.5)' }}>
                    <Heart size={12} fill={savedIds.has(video.id) ? '#fff' : 'none'} style={{ color: '#fff' }} />
                  </button>
                </div>
                <div className="p-3">
                  <p className="text-sm font-700 text-white line-clamp-2 leading-tight">{video.title}</p>
                  <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.5)' }}>{video.creator}</p>
                  <div className="flex items-center gap-3 mt-2">
                    <div className="flex items-center gap-1">
                      <Eye size={10} style={{ color: 'rgba(255,255,255,0.35)' }} />
                      <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.45)' }}>{video.views.toLocaleString()} views</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock size={10} style={{ color: 'rgba(255,255,255,0.35)' }} />
                      <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.45)' }}>{video.posted}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {video.tags.slice(0, 2).map((t) =>
                  <span key={t} className="text-[9px] px-1.5 py-0.5 rounded-md font-600"
                  style={{ background: 'rgba(255,59,92,0.12)', color: '#FF8A80' }}>{t}</span>
                  )}
                  </div>
                  <Link href="/login"
                className="mt-3 flex items-center justify-center gap-1 py-2 rounded-xl text-xs font-700"
                style={{ background: 'rgba(255,59,92,0.12)', color: '#FF3B5C', border: '1px solid rgba(255,59,92,0.2)' }}>
                    Watch Now <ChevronRight size={11} />
                  </Link>
                </div>
              </div>
            )}
          </div>

          <div className="mt-8 p-6 rounded-3xl text-center"
          style={{ background: 'linear-gradient(135deg, rgba(255,59,92,0.1), rgba(255,109,0,0.1))', border: '1px solid rgba(255,59,92,0.2)' }}>
            <h3 className="text-lg font-800 text-white mb-2">Upload Your Videos</h3>
            <p className="text-sm mb-4" style={{ color: 'rgba(255,255,255,0.5)' }}>Share your content with thousands of viewers across Kenya. Earn from your videos.</p>
            <Link href="/login"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl font-700 text-sm"
            style={{ background: '#FF3B5C', color: '#fff' }}>
              <Upload size={16} /> Start Uploading
            </Link>
          </div>
        </div>
      </div>
    </AppLayout>);

}